// Session stems export: renders each track in isolation (with its own FX/pan/
// fader) to a full-length, time-aligned WAV, then zips the stems together with a
// human-readable EDL. This is the practical interchange for moving a session
// into another DAW — drop every stem at 00:00 and the arrangement lines up.
import { Project, windowedNotes } from "../state/model";
import { createEffectNode, ensureDenoiseWorklet, ensureDeverbWorklet, chainLatency } from "../audio/effects";
import { createInstrument } from "../audio/gm";
import { wavBytes } from "./export-wav";
import { fmtTime } from "../util";
import { zipSync, strToU8 } from "fflate";

function sessionLength(project: Project): number {
  let len = 0;
  for (const t of project.tracks)
    for (const c of t.clips)
      if (c.takeActive !== false) len = Math.max(len, c.start + c.duration);
  return Math.max(len, 1);
}

async function renderTrack(
  project: Project,
  buffers: Map<string, AudioBuffer>,
  track: Project["tracks"][number],
  sampleRate: number,
  lengthSec: number,
): Promise<AudioBuffer> {
  const baseLen = Math.ceil(lengthSec * sampleRate);
  // Render extra head room for any denoise-insert latency, then trim it so the
  // stem stays aligned to 00:00 (plugin-delay-compensation).
  const probe = new OfflineAudioContext(1, 1, sampleRate);
  const denoiseReady = await ensureDenoiseWorklet(probe);
  const deverbReady = await ensureDeverbWorklet(probe);
  const lat = chainLatency(track.fx, denoiseReady, deverbReady);

  const ctx = new OfflineAudioContext(2, baseLen + lat, sampleRate);
  await ensureDenoiseWorklet(ctx);
  await ensureDeverbWorklet(ctx);
  const fxInput = ctx.createGain();
  const panner = ctx.createStereoPanner();
  panner.pan.value = track.pan;
  const fader = ctx.createGain();
  fader.gain.value = track.volume;
  let cursor: AudioNode = fxInput;
  for (const fx of track.fx) {
    if (fx.kind === "wam" || !fx.enabled) continue;
    const n = createEffectNode(ctx, fx);
    cursor.connect(n.input); cursor = n.output;
  }
  cursor.connect(panner); panner.connect(fader); fader.connect(ctx.destination);

  const synth = track.kind === "instrument" && track.instrument ? createInstrument(ctx, track.instrument, buffers) : null;
  if (synth) { synth.output.connect(fxInput); if (synth.ready) await synth.ready; }

  for (const clip of track.clips) {
    if (clip.takeActive === false) continue;
    if (clip.notes) {
      if (synth) for (const n of windowedNotes(clip)) synth.scheduleNote(n.midi, n.vel, clip.start + n.start, n.dur);
      continue;
    }
    const buf = buffers.get(clip.assetId);
    if (!buf) continue;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const cg = ctx.createGain();
    const startAt = clip.start;
    cg.gain.setValueAtTime(clip.gain, startAt);
    if (clip.fadeIn > 0) {
      cg.gain.setValueAtTime(0, startAt);
      cg.gain.linearRampToValueAtTime(clip.gain, startAt + clip.fadeIn);
    }
    if (clip.fadeOut > 0) {
      cg.gain.setValueAtTime(clip.gain, Math.max(startAt, startAt + clip.duration - clip.fadeOut));
      cg.gain.linearRampToValueAtTime(0, startAt + clip.duration);
    }
    src.connect(cg); cg.connect(fxInput);
    src.start(startAt, clip.offset, clip.duration);
  }
  const rendered = await ctx.startRendering();
  if (lat === 0) return rendered;
  const out = new AudioBuffer({ numberOfChannels: 2, length: baseLen, sampleRate });
  for (let ch = 0; ch < 2; ch++) out.copyToChannel(rendered.getChannelData(ch).subarray(lat, lat + baseLen), ch);
  return out;
}

const sanitize = (s: string) => s.replace(/[^\w\-]+/g, "_") || "track";

export async function exportStems(
  project: Project,
  buffers: Map<string, AudioBuffer>,
  sampleRate = 48000,
): Promise<Blob> {
  const lengthSec = sessionLength(project);
  const files: Record<string, Uint8Array> = {};
  const used = new Set<string>();

  let edl = `WebDAW session stems\n` +
    `====================\n` +
    `Project:     ${project.name}\n` +
    `Tempo:       ${project.tempo} BPM (${project.beatsPerBar}/4)\n` +
    `Sample rate: ${sampleRate} Hz, 24-bit WAV\n` +
    `Length:      ${fmtTime(lengthSec)} (${lengthSec.toFixed(3)} s)\n\n` +
    `All stems are the full session length and aligned to 00:00.\n` +
    `Import them onto separate tracks starting at the session start and the\n` +
    `arrangement will line up exactly. Stems are printed post per-track FX,\n` +
    `pan and fader, but pre master bus.\n\n` +
    `Clip map (start - end  name):\n`;

  let idx = 1;
  for (const track of project.tracks) {
    const hasContent = track.clips.some((c) => c.takeActive !== false && (buffers.get(c.assetId) || (c.notes && c.notes.length)));
    if (!hasContent) continue;
    const buf = await renderTrack(project, buffers, track, sampleRate, lengthSec);
    let name = `${String(idx).padStart(2, "0")}_${sanitize(track.name)}`;
    while (used.has(name)) name += "_";
    used.add(name);
    files[`stems/${name}.wav`] = wavBytes(buf, 24);

    edl += `\nTrack ${idx}: ${track.name}  (vol ${track.volume.toFixed(2)}, pan ${track.pan.toFixed(2)}${track.mute ? ", MUTED" : ""})\n`;
    for (const c of track.clips) {
      if (c.takeActive === false) continue;
      edl += `   ${fmtTime(c.start)} - ${fmtTime(c.start + c.duration)}  ${c.name}\n`;
    }
    idx++;
  }

  if (idx === 1) edl += `\n(No audio clips to export.)\n`;

  files["session.txt"] = strToU8(edl);
  files["README.txt"] = strToU8(
    `These are time-aligned session stems exported from WebDAW.\n\n` +
    `How to use:\n` +
    `1. Create one track per WAV in your DAW.\n` +
    `2. Import each WAV at the session start (00:00 / bar 1).\n` +
    `3. Everything will be in sync; mix from there.\n\n` +
    `See session.txt for tempo, the clip map, and per-track gain/pan.\n\n` +
    `Why stems instead of OMF/AAF? Those are legacy binary interchange\n` +
    `containers; time-aligned stems import reliably into any DAW.\n`,
  );

  const zipped = zipSync(files, { level: 6 });
  return new Blob([zipped as BlobPart], { type: "application/zip" });
}
